/**
 * Draft Service
 * Service layer for draft CRUD operations, designed for use with React Query
 */

import { supabase } from '@/integrations/supabase/client';
import type { DraftLoadResponse } from '@/types/drafts';

export interface Draft {
  id: string;
  user_id: string;
  form_slug: string;
  step: string | null;
  answers: Record<string, any>;
  updated_at: string;
}

export interface SaveDraftParams {
  userId: string;
  formSlug: string;
  step?: string | null;
  answers: Record<string, any>;
}

export interface DeleteDraftParams {
  userId: string;
  formSlug: string;
  step?: string | null;
}

/**
 * Fetch a draft by user ID and form slug
 */
export async function getDraft(
  userId: string,
  formSlug: string
): Promise<DraftLoadResponse | null> {
  if (!userId) {
    return null;
  }

  const { data, error } = await supabase
    .from('user_drafts')
    .select('step, answers, updated_at')
    .eq('user_id', userId)
    .eq('form_slug', formSlug)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Failed to load draft:', error);
    throw new Error(`Failed to load draft: ${error.message}`);
  }

  if (!data) {
    return null;
  }

  // Safely handle the answers field
  let answers: Record<string, any> = {};
  if (data.answers && typeof data.answers === 'object' && !Array.isArray(data.answers)) {
    answers = data.answers as Record<string, any>;
  }

  return {
    step: data.step,
    answers,
    lastUpdated: data.updated_at,
  };
}

/**
 * Save (upsert) a draft
 */
export async function saveDraft(params: SaveDraftParams): Promise<void> {
  const { userId, formSlug, step = null, answers } = params;

  if (!userId) {
    throw new Error('User ID is required to save draft');
  }

  const { error } = await supabase
    .from('user_drafts')
    .upsert(
      {
        user_id: userId,
        form_slug: formSlug,
        step,
        answers,
        updated_at: new Date().toISOString(),
      },
      {
        onConflict: 'user_id,form_slug,step',
      }
    );

  if (error) {
    console.error('Failed to save draft:', error);
    throw new Error(`Failed to save draft: ${error.message}`);
  }
}

/**
 * Delete a draft
 */
export async function deleteDraft(params: DeleteDraftParams): Promise<void> {
  const { userId, formSlug, step } = params;

  if (!userId) {
    throw new Error('User ID is required to delete draft');
  }

  let query = supabase
    .from('user_drafts')
    .delete()
    .eq('user_id', userId)
    .eq('form_slug', formSlug);

  if (step !== undefined) {
    query = query.eq('step', step);
  }

  const { error } = await query;

  if (error) {
    console.error('Failed to delete draft:', error);
    throw new Error(`Failed to delete draft: ${error.message}`);
  }
}

/**
 * Get all drafts for a user
 */
export async function getUserDrafts(userId: string): Promise<Draft[]> {
  if (!userId) {
    return [];
  }

  const { data, error } = await supabase
    .from('user_drafts')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Failed to get user drafts:', error);
    return [];
  }

  return (data || []) as Draft[];
}

// Export the draftService object for convenient access
export const draftService = {
  getDraft,
  saveDraft,
  deleteDraft,
  getUserDrafts,
};
